import { NgClass } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ClientSubscriptionService,
  TimeconversionService,
  ToastMessageService,
  AdvancedSettingService,
  AuthSevices,
} from '@fitness-force/api';
import type {
  ChangeStartDateRequest,
  ChangeEndDateRequest,
  RecurringTemplateNonRecurringService,
  SubscriptionById,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';

import { Observable } from 'rxjs';

@Component({
  selector: 'subscription-start-end-date-edit',
  templateUrl: './subscription-start-end-date-edit.component.html',
  providers: [],
  standalone: true,
  // styles: [
  //   `
  //     :host ::ng-deep .subscription-date-modal .p-dialog {
  //       max-height: 90vh !important;
  //       max-width: 95vw !important;
  //       display: flex !important;
  //       flex-direction: column !important;
  //     }

  //     :host ::ng-deep .subscription-date-modal .p-dialog-content {
  //       flex: 1 !important;
  //       overflow: hidden !important;
  //       padding: 0 !important;
  //       display: flex !important;
  //       flex-direction: column !important;
  //     }

  //     :host ::ng-deep .subscription-date-modal .overflow-y-auto {
  //       overflow-x: hidden !important;
  //       overflow-y: auto !important;
  //     }

  //     :host ::ng-deep .subscription-date-modal .p-datepicker-trigger,
  //     :host ::ng-deep .subscription-date-modal .fa,
  //     :host ::ng-deep .subscription-date-modal i {
  //       flex-shrink: 0 !important;
  //       width: auto !important;
  //       height: auto !important;
  //     }
  //   `,
  // ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    DatePickerModule,
    MessageModule,
    TranslateModule,
    NgClass,
  ],
})
export class SubscriptionStartEndDateEditComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private toastMessageService = inject(ToastMessageService);
  translate = inject(TranslateService);
  private clientSubscriptionService = inject(ClientSubscriptionService);
  private timeService = inject(TimeconversionService);
  private advanceSettingService = inject(AdvancedSettingService);
  private authService = inject(AuthSevices);

  readonly visible = input(false);
  readonly subscription = input<SubscriptionById>();
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly membershipId = input<number>(0);
  readonly closeEvent = output<any>();
  readonly reloadSubscription = output<any>();

  EditSubscriptionDatesForm: UntypedFormGroup = new UntypedFormGroup({});
  isFormSubmitting: boolean = false;

  dateLocked: boolean = true;
  membership: RecurringTemplateNonRecurringService;
  startDateChanged: boolean = true;
  maxServiceEndDate: any;
  dateError: boolean = false;

  memberships: RecurringTemplateNonRecurringService[] = [];

  subscriptionStartDate: Date;
  subscriptionEndDate: Date;
  maxSubscriptionStartDate: Date;
  private currentLoggedinUser: any;

  ngOnInit(): void {
    this.currentLoggedinUser = this.authService.currentUserDetails();
    this.getTenantSettings();
    this.componentInit();
  }

  getTenantSettings() {
    this.advanceSettingService
      .getAdvanceSetting(
        this.tenantId(),
        'limit_subscription_future_start_in_days',
      )
      .subscribe((res: any) => {
        this.maxSubscriptionStartDate = new Date(
          new Date().setDate(new Date().getDate() + parseInt(res.value)),
        );
      });
  }

  componentInit() {
    const subscriptionData = this.subscription();
    if (!subscriptionData) {
      return;
    }
    this.subscriptionStartDate = this.timeService.ConvertUTCToLocalDateTime(
      subscriptionData.start_date,
    );
    this.subscriptionEndDate = this.timeService.ConvertUTCToLocalDateTime(
      subscriptionData.end_date,
    );
    this.memberships =
      subscriptionData.recurring_template_non_recurring_services;
    if (this.membershipId() > 0) {
      this.membership = this.memberships.find(
        (o) => o.client_membership_id == this.membershipId(),
      )!;
      this.EditSubscriptionDatesForm = this.fb.group({
        start_date: [this.membership.service_start_date, Validators.required],
        end_date: [
          { value: this.membership.service_end_date, disabled: true },
          Validators.required,
        ],
        comment: [''],
      });
    } else {
      const subscriptionData = this.subscription();
      if (!subscriptionData) {
        return;
      }
      this.EditSubscriptionDatesForm = this.fb.group({
        start_date: [
          this.timeService.ConvertUTCToLocalDateTime(
            subscriptionData.start_date,
          ),
          Validators.required,
        ],
        end_date: [
          {
            value: this.timeService.ConvertUTCToLocalDateTime(
              subscriptionData.end_date,
            ),
            disabled: true,
          },
          Validators.required,
        ],
        comment: [''],
      });
    }
    const maxServiceDate = this.memberships.reduce((first, second) =>
      first.service_end_date > second.service_end_date ? first : second,
    );
    this.maxServiceEndDate = new Date(
      maxServiceDate!.service_end_date,
    ).setHours(0, 0, 0, 0);
  }

  toggleDateLock(dateLocked: boolean) {
    this.dateLocked = !dateLocked;
    if (this.membershipId() > 0) {
      this.StartDateFormControl.setValue(
        new Date(this.membership.service_start_date),
      );
      this.EndDateFormControl.setValue(
        new Date(this.membership.service_end_date),
      );
    } else {
      const subscription = this.subscription();
      if (!subscription) {
        return;
      }
      this.StartDateFormControl.setValue(new Date(subscription.start_date));
      this.EndDateFormControl.setValue(new Date(subscription.end_date));
    }
    if (this.dateLocked) {
      this.EndDateFormControl.disable();
      this.StartDateFormControl.enable();
    } else {
      this.EndDateFormControl.enable();
      this.StartDateFormControl.disable();
    }

    this.startDateChanged = true;
  }

  OnStartDateChange() {
    const selectedDate = new Date(this.StartDateFormControl.value);
    let previousStartDate = new Date();
    if (this.membershipId() > 0)
      previousStartDate = new Date(this.membership.service_start_date);
    else {
      const subscriptionData = this.subscription();
      if (subscriptionData) {
        previousStartDate = new Date(subscriptionData.start_date);
      }
    }

    const dateDiff = this.calculateDiff(previousStartDate, selectedDate);
    const existingEndDate = new Date(this.EndDateFormControl.value);
    const newEndDate = new Date(
      existingEndDate.setDate(existingEndDate.getDate() + dateDiff),
    ).setHours(0, 0, 0, 0);

    if (this.membershipId() > 0) {
      if (
        newEndDate > new Date(this.subscriptionEndDate).setHours(0, 0, 0, 0)
      ) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      } else if (
        selectedDate.setHours(0, 0, 0, 0) <
        new Date(this.subscriptionStartDate).setHours(0, 0, 0, 0)
      ) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB_START_DATE')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      } else {
        const updatedNewDate = new Date(
          new Date(newEndDate).setHours(
            new Date().getHours(),
            new Date().getMinutes(),
            new Date().getSeconds(),
            new Date().getMilliseconds(),
          ),
        );
        this.EndDateFormControl.setValue(updatedNewDate);
      }
    } else {
      if (new Date(newEndDate) < new Date(this.maxServiceEndDate)) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_SUBS')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      } else {
        const updatedNewDate = new Date(
          new Date(newEndDate).setHours(
            new Date().getHours(),
            new Date().getMinutes(),
            new Date().getSeconds(),
            new Date().getMilliseconds(),
          ),
        );
        this.EndDateFormControl.setValue(updatedNewDate);
      }
    }
    this.startDateChanged = true;
  }

  OnEndDateChange(event: any) {
    const EndDate = new Date(this.EndDateFormControl.value);
    const newEndDate = new Date(EndDate).setHours(0, 0, 0, 0);
    if (this.membershipId() > 0) {
      if (
        newEndDate > new Date(this.subscriptionEndDate).setHours(0, 0, 0, 0)
      ) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      }
    } else {
      // if (newEndDate < this.maxServiceEndDate) {
      //   this.translate
      //     .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_SUBS')
      //     .subscribe((translation) => {
      //       this.toastMessageService.showErrorMessage(translation);
      //     });
      // }
    }
    this.startDateChanged = false;
  }

  calculateDiff(previousDate: Date, currentDate: Date) {
    return Math.floor(
      (Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      ) -
        Date.UTC(
          previousDate.getFullYear(),
          previousDate.getMonth(),
          previousDate.getDate(),
        )) /
        (1000 * 60 * 60 * 24),
    );
  }

  UpdateStartEndDate() {
    if (this.EditSubscriptionDatesForm.valid) {
      if (this.membershipId() > 0) {
        this.changeMembershipDates();
      } else {
        this.changeSubscriptionDates();
      }
    } else {
      Object.keys(this.EditSubscriptionDatesForm.controls).forEach((field) => {
        const control = this.EditSubscriptionDatesForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  changeMembershipDates() {
    this.isFormSubmitting = true;
    let service = new Observable<any>();
    if (this.startDateChanged) {
      this.StartDateFormControl.value.setHours(0, 0, 0);
      const editDateRequest: ChangeStartDateRequest =
        {} as ChangeStartDateRequest;
      editDateRequest.start_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.StartDateFormControl.value,
      );
      editDateRequest.comment = this.CommentFormControl.value;
      const subscriptionData = this.subscription();
      if (!subscriptionData) {
        return;
      }
      service = this.clientSubscriptionService.ChangeMembershipStartDate(
        subscriptionData.tenant_id,
        this.clientId(),
        subscriptionData.id,
        this.membershipId(),
        editDateRequest,
      );
    } else {
      this.EndDateFormControl.value.setHours(23, 59, 59);
      const editDateRequest: ChangeEndDateRequest = {} as ChangeEndDateRequest;
      editDateRequest.end_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.EndDateFormControl.value,
      );
      editDateRequest.comment = this.CommentFormControl.value;
      const subscriptionData = this.subscription();
      if (!subscriptionData) {
        return;
      }
      service = this.clientSubscriptionService.ChangeMembershipEndDate(
        subscriptionData.tenant_id,
        this.clientId(),
        subscriptionData.id,
        this.membershipId(),
        editDateRequest,
      );
    }
    service.subscribe({
      next: async (res: any) => {
        this.isFormSubmitting = false;
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.SUCCESS_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        await this.timeout(2000);
        const subscriptionData = this.subscription();
        if (subscriptionData) {
          subscriptionData.recurring_template_non_recurring_services.forEach(
            (o) => {
              if (o.client_membership_id == this.membershipId()) {
                o.service_start_date = this.StartDateFormControl.value;
                o.service_end_date = this.EndDateFormControl.value;
              }
            },
          );
        }
        this.closePopup(true);
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  changeSubscriptionDates() {
    this.isFormSubmitting = true;
    let service = new Observable<any>();
    if (this.startDateChanged) {
      this.StartDateFormControl.value.setHours(0, 0, 0);
      const editDateRequest: ChangeStartDateRequest =
        {} as ChangeStartDateRequest;
      editDateRequest.start_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.StartDateFormControl.value,
      );
      editDateRequest.comment = this.CommentFormControl.value;
      const subscriptionData = this.subscription();
      if (!subscriptionData) {
        return;
      }
      service = this.clientSubscriptionService.ChangeSubscriptionStartDate(
        subscriptionData.tenant_id,
        this.clientId(),
        subscriptionData.id,
        editDateRequest,
      );
    } else {
      this.EndDateFormControl.value.setHours(23, 59, 59);
      const editDateRequest: ChangeEndDateRequest = {} as ChangeEndDateRequest;
      editDateRequest.end_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.EndDateFormControl.value,
      );
      editDateRequest.comment = this.CommentFormControl.value;
      const subscriptionData = this.subscription();
      if (!subscriptionData) {
        return;
      }
      service = this.clientSubscriptionService.ChangeSubscriptionEndDate(
        subscriptionData.tenant_id,
        this.clientId(),
        subscriptionData.id,
        editDateRequest,
      );
    }
    service.subscribe({
      next: async (res: any) => {
        this.isFormSubmitting = false;
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.SUCCESS_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        await this.timeout(2000);
        const subscription = this.subscription();
        if (subscription) {
          subscription.start_date =
            this.StartDateFormControl.value.toUTCString();
          subscription.end_date = this.EndDateFormControl.value.toUTCString();
        }
        this.reloadData(true);
        this.closePopup(true);
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  closePopup(value: boolean) {
    this.closeEvent.emit(this.subscription());
    this.closeEvent.emit(this.subscription());
  }

  reloadData(value: boolean) {
    this.reloadSubscription.emit(value);
  }

  get StartDateFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .start_date as UntypedFormControl;
  }

  get EndDateFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .end_date as UntypedFormControl;
  }

  get CommentFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .comment as UntypedFormControl;
  }

  canBypassMaxSubscriptionStartDate(): boolean {
    return (
      this.currentLoggedinUser &&
      (this.currentLoggedinUser.role.toLowerCase() == 'admin' ||
        this.currentLoggedinUser.can_admin_billing == 'true')
    );
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
